import React, { useEffect, useMemo, useState } from 'react'
import { View, TouchableOpacity, StyleSheet, Dimensions, Platform } from 'react-native'
import Colors from '../../../constants/Colors'
import GlobalStyle, {
	MAX_CHECKOUT_WIDTH,
	smallTextSize,
	xlTextSize,
	IS_LANDSCAPE,
	largeTextSize,
	SMALL_WIDTH,
} from '../../../constants/GlobalStyle'
import { AntDesign } from '@expo/vector-icons'
import { Text } from '../../../components/Themed'
import Banner from '../../../components/Banner'
import { useAppDispatch, useAppSelector } from '../../../state/hooks'
import { showAlert } from '../../../state/Slices/settingsSlice'
import { setCheckoutState } from '../../../state/Slices/checkoutSlice'
import {
	ChangeLocationBox,
	DeliveryDetailsBox,
	PickupPointDetailsBox,
	PickupTimeBox,
	RewardsBox,
} from './SelectorBox'
import { styles as helperStyles } from '../Helpers/style'
import { prepText } from '../../../helpers/prepText'
import { useOrganizationStyles } from '../../../hooks/useOrganizationStyles'
import { INVALID_ADDRESS_TEXT, NO_ADDRESS_TEXT } from './UI'
import { areAllRestaurantsInCartOpen } from '../../../helpers/restaurantHours'
import { useGoogleAnalyticsContext } from '../../../context/GoogleAnalyticsContext'
import { useRetrieveDataContext } from '../../../context/RetrieveDataContext'

// Mobile & web -> show cart prices (not sticky)
const CartFooter: React.FC<any> = ({
	prices,
	hasInvalidAddress,
	hasSelectedAddress,
	style = {},
	showBanner = false,
	useColumnView = false,
	isRestaurantSnoozed = false,
	onCartPress = null,
	navigation = null,
	singleBrandEnabled = null,
	restaurantList = null,
	setLocationsModalVisible = null,
	locationsModalVisible = null,
	formattedAddressLine = null,
	pickupPointModalVisible = null,
	setPickupPointModalVisible = null,
	filteredRestaurantsByItems = null,
	hoursRef,
}) => {
	const { delivery, deliveryEnabled, webOrdersEnabled, allowOrdersOutsideOrgHours, localTimezone } =
		useAppSelector(state => state.settings)
	const { triggerValidateCartItems } = useRetrieveDataContext()
	const { cart, cartPrep, isDelivery, isTestMode, deliveryEstimate } = useAppSelector(
		state => state.persisted
	)
	const { deviceHasInternet, userId } = useAppSelector(state => state.user)
	const { restaurants, loadedData } = useAppSelector(state => state.data)
	const { showInvalidAddress } = useAppSelector(state => state.checkout)
	const { hasBoxShadow } = useOrganizationStyles()
	const dispatch = useAppDispatch()
	useEffect(() => {
		if (Platform.OS === 'web') {
			const return_step = new URL(window.location.href).searchParams.get('return_step')
			if (return_step === 'basic_info') {
				window.setTimeout(() => onCartPress(), 100)
			}
		}
	}, [window.location.href])

	const onCheckoutPress = () => {
		return isRestaurantSnoozed
			? () =>
					dispatch(
						showAlert({
							title: 'Not taking orders right now!',
							body: 'Please try again later.',
						})
					)
			: !deviceHasInternet
				? () =>
						dispatch(
							showAlert({
								title: 'Cannot connect to server!',
								body: 'Please test your connection and try again.',
							})
						)
				: () => {
						if (isDelivery && (hasInvalidAddress || !hasSelectedAddress)) {
							dispatch(
								setCheckoutState({
									showInvalidAddress: true,
								})
							)
						} else {
							captureBeginCheckoutEvent()
							dispatch(
								setCheckoutState({
									showInvalidAddress: false,
								})
							)
							onCartPress()
						}
					}
	}
	const { trackBeginCheckout } = useGoogleAnalyticsContext()

	const [showTaxDetails, setShowTaxDetails] = useState(false)
	const cartOpen = areAllRestaurantsInCartOpen(
		cart,
		restaurants,
		loadedData[global.org].hours ?? null,
		allowOrdersOutsideOrgHours,
		localTimezone
	)
	const captureBeginCheckoutEvent = () => {
		trackBeginCheckout({ cart })
	}

	function Taxes() {
		var taxes = []

		prices.taxesList.forEach(tax => {
			var index = taxes.findIndex(t => t.name === tax.name)
			if (index >= 0) {
				taxes[index] = {
					id: tax.id,
					name: tax.name,
					amount: taxes[index].amount + tax.amount,
				}
			} else {
				//doesnt exist
				taxes.push({
					id: tax.id,
					name: tax.name,
					amount: tax.amount,
				})
			}
		})
		const taxReturn = []
		taxes.forEach((tax, i) => {
			taxReturn.push(generateRow(tax.name, tax.amount / 100, true, useColumnView))
		})
		return (
			<>
				{taxReturn.map((tax, i) => (
					<View key={tax.name + tax.amount + '_' + i}>{tax}</View>
				))}
			</>
		)
	}
	const invalidAddress =
		hasSelectedAddress &&
		isDelivery &&
		(deliveryEstimate.estimate.totalFee === -1 || deliveryEstimate.estimate.timeToDeliver === -1)

	const noAddress = useMemo(() => {
		return isDelivery && !hasSelectedAddress
	}, [isDelivery, hasSelectedAddress])

	const isCheckoutDisabled = useMemo(() => {
		return (
			!deviceHasInternet ||
			isRestaurantSnoozed ||
			showBanner ||
			noAddress ||
			invalidAddress ||
			(Platform.OS === 'web' && !webOrdersEnabled && !isTestMode)
		)
	}, [deviceHasInternet, isRestaurantSnoozed, showBanner, noAddress, invalidAddress])

	useEffect(() => {
		if (!isDelivery) {
			dispatch(
				setCheckoutState({
					showInvalidAddress: false,
				})
			)
		}
	}, [isDelivery])

	console.log(
		Math.round(prices.serviceCharge * 100) / 100,
		Math.round(prices.serviceChargeGST * 100) / 100,
		Math.round(prices.serviceChargePST * 100) / 100,
		Math.round(prices.serviceFeeTax * 100) / 100,
		prices.bagFee,
		prices.bagFeeTax,
		prices.deliveryFee / 100
	)

	return (
		<>
			<View
				style={[
					style,
					{
						backgroundColor: Colors.light.background,
						padding: 16,
						...(hasBoxShadow
							? {
									shadowColor: '#000',
									shadowOffset: { width: 0, height: 2 },
									shadowOpacity: 0.25,
									shadowRadius: 3.84,
									elevation: 5,
								}
							: {}),
					},
					useColumnView && {
						borderRadius: 16,
						borderWidth: 1,
						borderColor: Colors.greyscale[4],
					},
				]}
			>
				<View style={{ display: cart.length ? 'flex' : 'none' }}>
					{global.kiosk || !loadedData ? null : (
						<>
							<View
								style={[{ marginTop: 8 }, useColumnView && { marginHorizontal: '3.5%' }]}
							></View>
							{loadedData[global.org].loyaltyEnabled && <RewardsBox navigation={navigation} />}

							<View
								style={{
									alignSelf: 'center',
									maxWidth: MAX_CHECKOUT_WIDTH,
									width: '100%',
								}}
							>
								{singleBrandEnabled && restaurantList.length > 1 && !isDelivery && (
									<ChangeLocationBox
										toggleLocationModal={() => {
											setLocationsModalVisible(!locationsModalVisible)
										}}
										currentAddress={formattedAddressLine}
									/>
								)}

								<DeliveryDetailsBox
									errorMessages={
										<>
											{invalidAddress ? (
												<View style={{ marginBottom: 16 }}>
													<Text
														style={{
															color: 'red',
														}}
													>
														{INVALID_ADDRESS_TEXT}
													</Text>
												</View>
											) : null}
											{noAddress && (
												<View style={{ marginBottom: 16 }}>
													<Text
														style={{
															color: 'red',
														}}
													>
														{NO_ADDRESS_TEXT}
													</Text>
												</View>
											)}
										</>
									}
								/>

								<PickupPointDetailsBox
									showModal={pickupPointModalVisible}
									setShowModal={setPickupPointModalVisible}
									numOfRestaurants={filteredRestaurantsByItems?.length}
								/>

								<PickupTimeBox
									toggleTimeModal={() => {
										hoursRef.current.open()
									}}
									pickupText={prepText(cartPrep, isDelivery)}
									isOpen={cartOpen}
								/>
							</View>
						</>
					)}
				</View>
				<View
					style={[
						styles.summaryContainer,
						{
							alignSelf: 'center',
						},
					]}
				>
					{generateRow('Subtotal', prices.subTotal + prices.noCodeDiscount, false, useColumnView)}
					{generateRow(
						'Taxes & Fees',
						prices.taxes +
							Math.round(prices.serviceCharge * 100) / 100 +
							Math.round(prices.serviceChargeGST * 100) / 100 +
							Math.round(prices.serviceChargePST * 100) / 100 +
							Math.round(prices.serviceFeeTax * 100) / 100 +
							prices.bagFee +
							prices.bagFeeTax +
							prices.deliveryFee / 100,
						false,
						useColumnView,
						() => setShowTaxDetails(!showTaxDetails)
					)}
					{showTaxDetails && (
						<View style={{ marginLeft: 15, marginRight: 80, marginBottom: 5 }}>
							{prices.taxes !== 0 ? <Taxes /> : <View></View>}
							{prices.serviceCharge !== 0 &&
								generateRow(
									`Service Fee`,
									Math.round(prices.serviceCharge * 100) / 100 +
										Math.round(prices.serviceChargeGST * 100) / 100 +
										Math.round(prices.serviceChargePST * 100) / 100 +
										Math.round(prices.serviceFeeTax * 100) / 100,
									true,
									useColumnView
								)}

							{prices.bagFee !== 0 &&
								generateRow('Bag Fee', prices.bagFee + prices.bagFeeTax, true, useColumnView)}
							{isDelivery &&
								deliveryEnabled &&
								generateRow(
									'Delivery Fee',
									prices.deliveryFee > 0
										? (prices.deliveryFee + deliveryEstimate.estimate.deliveryTax) / 100
										: 0,
									true,
									useColumnView
								)}
						</View>
					)}
					{generateRow('Discounts', prices.discount, false, useColumnView)}
					{prices.tip > 0 && generateRow('Tip', prices.tip, false, useColumnView)}
					{prices.redeemedGiftCard !== 0 &&
						generateRow('Gift Card', prices.redeemedGiftCard, false, useColumnView)}

					<View style={[styles.totalContainer, { paddingTop: useColumnView ? 8 : '1%' }]}>
						<Text style={styles.totalText}>TOTAL DUE</Text>
						<Text style={[styles.totalText, { color: '#57B042' }]}>${prices.total.toFixed(2)}</Text>
					</View>
					{showBanner ? (
						<View style={{ marginBottom: 20 }}>
							<View
								style={{ marginTop: 20, backgroundColor: Colors.light.background, borderRadius: 5 }}
							>
								<Banner
									text={`Minimum $${(delivery.minSpend / 100).toFixed(
										2
									)} required for delivery orders.`}
									textStyle={{ color: global.orgColor }}
									style={{ backgroundColor: 'rgba(255, 255, 0, 0.25)', marginTop: 0 }}
									showAddItemsButton={false}
								/>
							</View>
						</View>
					) : (
						<View style={{ marginBottom: 20 }}></View>
					)}
				</View>
				<View style={{ backgroundColor: Colors.light.background, borderRadius: 16 }}>
					<TouchableOpacity
						style={[
							GlobalStyle.v2ButtonCartRightArrow,
							{
								backgroundColor: isCheckoutDisabled ? Colors.greyscale[6] : global.orgColor2,
								opacity: isCheckoutDisabled ? 0.5 : 1,
							},
						]}
						onPress={
							isRestaurantSnoozed
								? () =>
										dispatch(
											showAlert({
												title: 'Not taking orders right now!',
												body: 'Please try again later.',
											})
										)
								: !deviceHasInternet
									? () =>
											dispatch(
												showAlert({
													title: 'Cannot connect to server!',
													body: 'Please test your connection and try again.',
												})
											)
									: async () => {
											if (isDelivery && (hasInvalidAddress || !hasSelectedAddress)) {
												dispatch(
													setCheckoutState({
														showInvalidAddress: true,
													})
												)
											} else {
												captureBeginCheckoutEvent()
												dispatch(
													setCheckoutState({
														showInvalidAddress: false,
													})
												)
												console.log('checkout now is pressed')
												const isValid = await triggerValidateCartItems()
												console.log('isValid', isValid)
												if (!isValid) return
												onCartPress()
											}
										}
						}
						disabled={isCheckoutDisabled}
					>
						<>
							<Text style={[styles.buttonText, { color: 'white', marginRight: 10 }]}>
								CHECKOUT NOW
							</Text>
							<AntDesign name="right" size={largeTextSize - 3} color="white" />
						</>
					</TouchableOpacity>
				</View>
			</View>
		</>
	)
}

// Kiosk / pos -> show cart prices (sticky)
// Also includes add promo code, add order notes buttons
export const KioskCartFooter = ({
	prices,
	priceSet,
	cost,
	couponCode,
	bagFeeEnabled,
	openPromo,
	setShowOrderNotes,
	checkoutFunc,
	style = {},
}) => {
	const { triggerValidateCartItems } = useRetrieveDataContext()
	const { deviceHasInternet } = useAppSelector(state => state.user)
	const { isDelivery } = useAppSelector(state => state.persisted)
	const { deliveryEnabled } = useAppSelector(state => state.settings)
	const [showTaxDetails, setShowTaxDetails] = useState(false)
	function Taxes() {
		var taxes = []

		prices.taxesList.forEach(tax => {
			var index = taxes.findIndex(t => t.name === tax.name)
			if (index >= 0) {
				taxes[index] = {
					id: tax.id,
					name: tax.name,
					amount: taxes[index].amount + tax.amount,
				}
			} else {
				//doesnt exist
				taxes.push({
					id: tax.id,
					name: tax.name,
					amount: tax.amount,
				})
			}
		})
		const taxReturn = []
		taxes.forEach((tax, i) => {
			taxReturn.push(generateRow(tax.name, tax.amount / 100, true))
		})
		return <>{taxReturn}</>
	}

	return (
		<View style={[styles.container, style]}>
			<View style={styles.summaryContainer}>
				{generateRow('Subtotal', prices.subTotal + prices.noCodeDiscount)}
				{generateRow(
					'Taxes & Fees',
					prices.taxes +
						Math.round(prices.serviceCharge * 100) / 100 +
						Math.round(prices.serviceChargeGST * 100) / 100 +
						Math.round(prices.serviceChargePST * 100) / 100 +
						Math.round(prices.serviceFeeTax * 100) / 100 +
						prices.bagFee +
						prices.bagFeeTax +
						prices.deliveryFee / 100,
					false,
					false,
					() => setShowTaxDetails(!showTaxDetails)
				)}
				{showTaxDetails && (
					<View style={{ marginLeft: 15, marginRight: 80, marginBottom: 5 }}>
						{prices.taxes !== 0 ? <Taxes /> : <View></View>}
						{prices.serviceCharge !== 0 &&
							generateRow(
								`Service Fee`,
								Math.round(prices.serviceCharge * 100) / 100 +
									Math.round(prices.serviceChargeGST * 100) / 100 +
									Math.round(prices.serviceChargePST * 100) / 100 +
									Math.round(prices.serviceFeeTax * 100) / 100,
								true
							)}
						{prices.bagFee !== 0 && generateRow('Bag Fee', prices.bagFee + prices.bagFeeTax, true)}
					</View>
				)}
				{generateRow('Discounts', prices.discount)}
				{prices.redeemedGiftCard !== 0 && generateRow('Gift Card', prices.redeemedGiftCard)}
				<View style={styles.totalContainer}>
					<Text style={styles.totalText}>TOTAL DUE</Text>
					<Text style={[styles.totalText, { color: '#57B042' }]}>${prices.total.toFixed(2)}</Text>
				</View>
			</View>

			<View style={styles.buttonContainer}>
				<TouchableOpacity
					style={[
						styles.checkoutButton,
						!deviceHasInternet && { backgroundColor: Colors.greyscale[6] },
					]}
					onPress={async () => {
						console.log('checkout now in item is pressed')
						const isValid = await triggerValidateCartItems()
						if (!isValid) return
						checkoutFunc()
					}}
					disabled={!deviceHasInternet}
				>
					<Text style={styles.checkoutButtonText}>CHECKOUT NOW</Text>
					<AntDesign name="right" size={xlTextSize} color="black" />
				</TouchableOpacity>
			</View>
		</View>
	)
}

const generateRow = (title, amount, small = false, useColumnView = false, onPress = undefined) => {
	return (
		<Row
			title={title}
			amount={`$${typeof amount === 'number' ? amount.toFixed(2) : amount}`}
			small={small}
			useColumnView={useColumnView}
			onPress={onPress}
		/>
	)
}

const Row = ({ title, amount, small, useColumnView = false, onPress }) => (
	<TouchableOpacity
		onPress={onPress}
		disabled={!onPress}
		style={[small ? styles.smallRow : styles.row, useColumnView && !small && { marginVertical: 8 }]}
	>
		<Text style={small ? styles.smallTitleText : styles.titleText}>{title}</Text>
		<Text style={small ? styles.smallAmountText : styles.amountText}>{amount}</Text>
	</TouchableOpacity>
)

const styles = StyleSheet.create({
	container: {
		flexDirection: 'row',
		paddingHorizontal:
			Dimensions.get('window').width > 500 ? Dimensions.get('window').width * 0.05 : '3%',
		paddingTop: IS_LANDSCAPE ? '1.5%' : '5%',
		paddingBottom: IS_LANDSCAPE ? '1.5%' : '5%',
		backgroundColor: Colors.greyscale[9],
	},
	summaryContainer: {
		flex: 11,
		...(Platform.OS === 'web' ? { width: '100%' } : {}),
		justifyContent: 'space-between',
	},
	row: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		marginVertical: 8,
	},
	smallRow: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		marginVertical: 2,
	},
	titleText: {
		color: Platform.OS === 'web' ? Colors.light.text : '#fff',
		fontSize: 16,
	},
	amountText: {
		color: Platform.OS === 'web' ? Colors.light.text : Colors.greyscale[2],
		fontSize: 16,
	},
	smallTitleText: {
		color: Platform.OS === 'web' ? Colors.light.text : '#fff',
		fontSize: 14,
	},
	smallAmountText: {
		color: Platform.OS === 'web' ? Colors.light.text : Colors.greyscale[2],
		fontSize: 14,
	},
	totalContainer: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		marginTop: 20,
		borderTopWidth: 1,
		borderTopColor: '#fff',
		paddingTop: IS_LANDSCAPE ? '3%' : '5%',
	},
	totalText: {
		textAlign: 'center',
		color: Platform.OS === 'web' ? Colors.greyscale[8] : 'white',
		fontWeight: '500',
		fontSize: 24,
		letterSpacing: 1,
	},
	buttonContainer: {
		flex: Dimensions.get('window').width > 500 ? 9 : 12,
		justifyContent: 'flex-end',
		marginLeft: '5%',
	},
	button: {
		borderRadius: 10,
		alignItems: 'center',
		justifyContent: 'center',
		paddingVertical: IS_LANDSCAPE ? '4%' : '7%',
		borderWidth: 1,
		borderColor: 'white',
		marginLeft: '25%',
		marginBottom: IS_LANDSCAPE ? '5%' : '10%',
	},
	buttonText: {
		color: 'black',
		fontSize: 24,
	},
	bagFeeButton: {
		borderRadius: 10,
		alignItems: 'center',
		justifyContent: 'center',
		paddingVertical: 10,
		borderWidth: 1,
		borderColor: 'white',
		marginBottom: 10,
	},
	checkoutButton: {
		flexDirection: 'row',
		borderRadius: smallTextSize,
		alignItems: 'center',
		justifyContent: 'center',
		paddingVertical: IS_LANDSCAPE ? '5%' : '8%',
		backgroundColor: 'white',
		paddingHorizontal: 5,
	},
	checkoutButtonText: {
		marginHorizontal: 10,
		textAlign: 'center',
		color: 'black',
		fontWeight: '400',
		fontSize: xlTextSize,
	},
})

export default CartFooter
